import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import {
    useIntl,
    FormattedMessage,
} from 'react-intl';

import {
    Colours,
    Sizes,
    StarRating,
    PrimaryAnchor,
    Locales,
    formatPhoneNumber,
    IconUser,
    PROVINCE,
} from '@ratehub/base-ui';

import { LOCALES } from '@ratehub/documents/common/v1';

import getApplicationUrlWithAgent from '../functions/getApplicationUrlWithAgent';


function AgentCard({ agent }) {
    const intl = useIntl();

    const fullName = `${agent.firstName} ${agent.lastName}`;
    // convert averageRating from a 0-5 scale to a 0-100 scale
    const agentRating = agent.averageRating / 5 * 100;
    const formattedPhoneNumber = !!agent.phone && formatPhoneNumber(agent.phone);
    const canApplyWithAgent = agent.selectable;

    return (
        <CardContainer
            className="rh-bg-coconut rh-display-grid rh-py-1 rh-px-1_25"
            data-name="mtg-agent-card-container"
        >
            <div className="agent-image-name-reviews-container rh-display-flex rh-mb-0_5">
                <a
                    href={getAgentPageUrl({
                        agent,
                        locale: intl.locale,
                    })}
                    alt={`${fullName}`}
                    className="agent-profile-link rh-text-decoration-none"
                >
                    <Choose>
                        <When condition={!!agent.image}>
                            <img
                                src={agent.image}
                                alt={`${fullName}`}
                                className="profile-image rh-mr-1 rh-border-width-1px rh-border-style-solid rh-border-color-stone-light rh-border-radius-50p"
                            />
                        </When>
                        <Otherwise>
                            <IconUser
                                className={`
                                    profile-image fallback-image
                                    rh-mr-1 rh-border-radius-50p
                                `}
                                fill={Colours.STONE_LIGHT}
                                stroke={Colours.STONE}
                                outlineWidth="0"
                            />
                        </Otherwise>
                    </Choose>
                </a>
                <div className="agent-name-reviews-container rh-display-flex">
                    <a
                        href={getAgentPageUrl({
                            agent,
                            locale: intl.locale,
                        })}
                        alt={`${fullName}`}
                        className="agent-profile-link rh-text-decoration-none rh-title-xs rh-mt-0 rh-mb-0_5"
                        data-name="mtg-agent-full-name"
                    >
                        {fullName}
                    </a>
                    <If condition={agent.title}>
                        <p
                            className="rh-text-xs rh-mt-0 rh-mb-0_25"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: agent.title,
                            }}
                            data-name="mtg-agent-title"
                        />
                    </If>
                    <If condition={agent.licenseNumber}>
                        <p
                            className="rh-text-xs rh-my-0"
                            data-name="mtg-agent-license-number"
                        >
                            <FormattedMessage
                                id="mtg-common.agentCard.licenseNumber"
                                defaultMessage="ON Lic. {licenseNumber}"
                                values={{
                                    licenseNumber: agent.licenseNumber,
                                }}
                            />
                        </p>
                    </If>
                </div>
            </div>
            <a
                href={getAgentPageUrl({
                    agent,
                    locale: intl.locale,
                    pageAnchor: 'reviews',
                })}
                alt={fullName}
                className="agent-review rh-text-decoration-none rh-display-flex rh-justify-content-center"
            >
                <StarRating
                    ratingPercent={agentRating}
                    starSize={Sizes.ICONS['2XS']}
                />
                <p
                    className="rh-text-xs weight-medium rh-mt-0 rh-mx-0_25"
                    data-name="mtg-agent-reviews"
                >
                    <FormattedMessage
                        id="mtg-common.agentCard.reviewCount"
                        defaultMessage={`{reviewCount, plural,
                            one {{reviewCount, number} review}
                            other {{reviewCount, number} reviews}
                        }`}
                        values={{
                            reviewCount: agent.reviewCount,
                        }}
                    />
                </p>
            </a>
            <If condition={agent.phone}>
                <div className="agent-phone-number rh-display-block rh-text-align-center rh-text-decoration-none rh-text-s rh-fg-blackberry-light rh-mb-0_5 rh-text-decoration-none">
                    <p
                        className="rh-my-0"
                        data-name="mtg-agent-phone"
                    >
                        <FormattedMessage
                            id="mtg-common.agentCard.phoneNumber"
                            defaultMessage={`{phoneNumber} {hasExtension, select,
                                    true {ext. {extension}}
                                    other {}
                                }
                            `}
                            values={{
                                phoneNumber: formattedPhoneNumber,
                                hasExtension: !!agent.phoneExt,
                                extension: agent.phoneExt,
                            }}
                        />
                    </p>
                </div>
            </If>
            <a
                href={`mailto:${agent.email}`}
                className="agent-email rh-display-block rh-text-align-center rh-text-decoration-none rh-title-3xs rh-fg-blackberry rh-mb-0 rh-text-decoration-none"
                data-name="mtg-agent-email"
            >
                { agent.email }
            </a>
            <Choose>
                <When condition={canApplyWithAgent}>
                    <PrimaryAnchor
                        data-name="mtg-apply-now-cta"
                        message={(
                            <FormattedMessage
                                id="mtg-common.agentCard.applyNow"
                                defaultMessage="apply now"
                            />
                        )}
                        href={getApplicationUrlWithAgent(intl.locale, agent.agentID)}
                        size="medium"
                        className="agent-apply-now rh-mt-1_25"
                    />
                </When>
                <Otherwise>
                    <PrimaryAnchor
                        data-name="mtg-about-agent-cta"
                        message={(
                            <FormattedMessage
                                id="mtg-common.agentCard.aboutAgent"
                                defaultMessage="about {agentName}"
                                values={{
                                    agentName: agent.firstName,
                                }}
                            />
                        )}
                        href={getAgentPageUrl({
                            agent,
                            locale: intl.locale,
                        })}
                        size="medium"
                        className="about-agent rh-mt-1_25"
                        shouldLowerCaseMessage={false}
                    />
                </Otherwise>
            </Choose>
        </CardContainer>
    );
}

AgentCard.propTypes = {
    agent: PropTypes.object.isRequired,
};

function getAgentPageUrl({ agent, locale = LOCALES.ENGLISH, pageAnchor = undefined }) {
    const { firstName, lastName, opportunityOwnerRegion } = agent;

    const pagePath = locale === Locales.FRENCH ? '/courtier-hypothecaire-a-ratehub-' : '/mortgage-agent-at-ratehub-';
    const region = opportunityOwnerRegion === PROVINCE.QUEBEC ? 'qc' : 'brokerage';
    const fullName = `${firstName}-${lastName}`.replace(/\s+/g, '-').toLowerCase();
    const urlPageAnchor = pageAnchor ? `#${pageAnchor}` : '';

    return `${pagePath}${region}/${fullName}${urlPageAnchor}`;
}

const linkStyling = css`
    color: ${Colours.BLACKBERRY};
    transition: all 0.2s ease-in;

    &{
        :hover {
            color: ${Colours.BLUEBERRY};
            text-decoration: underline;
        }

        :visited, :active {
            color: ${Colours.BLUEBERRY_DARKEST};
        }
    }
`;

const CardContainer = styled.li`
    box-shadow: 0 0 ${Sizes.SPACING.HALF} 0 ${rgba(Colours.STONE, 0.5)};
    border-radius: ${Sizes.SPACING.HALF};
    max-width: 25.75rem; // 412px at 16px font size this was the max width of the agent card that looked ideal during UAT

    > .agent-image-name-reviews-container {
        .agent-name-reviews-container {
            flex-direction: column;
        }

        .agent-profile-link {
            ${linkStyling}
        }

        .profile-image {
            width: 5rem;
            height: 5rem;

            // there is a possible bug where the profile image was being stretched, added max-width and max-height to prevent this
            max-width: 5rem;
            max-height: 5rem;

            object-fit: cover;
            object-position: 50% 0%;

            &.fallback-image {
                border: none;
                object-position: 50% 50%; // initial value
            }
        }
    }

    > .agent-email,
    > .agent-review {
        ${linkStyling}
    }

    > .agent-phone-number {
        // This is to reserve the space if a phone number is not present
        min-height: 1.3125rem; // 21px at 16px font size
    }

    > .agent-apply-now,
    > .about-agent {
        align-self: end;
    }
`;

export default AgentCard;
